// src/frontend/components/FloatingButton.tsx
import React, { useState, useEffect } from 'react';
import { KalicubeToolboxSettings, defaultSettings, DirectionalValue } from '@/types/settings';
import useScrollAnimation from '../hooks/useScrollAnimation';

// Define global window types for WordPress data
declare global {
  interface Window {
    kalicubeToolboxData?: Partial<KalicubeToolboxSettings>;
  }
}

const FloatingButton: React.FC = () => {
  const [settings, setSettings] = useState<Partial<KalicubeToolboxSettings>>(defaultSettings);
  const [hover, setHover] = useState<boolean>(false);
  // Get visibility state from useScrollAnimation hook
  const { isVisible, shouldAnimate } = useScrollAnimation({
    enabled: settings.scroll_animation_enabled || 'no',
    threshold: settings.scroll_threshold || 20,
    direction: settings.scroll_direction || 'down'
  });
  
  useEffect(() => {
    // Get settings from global window object
    const data = window.kalicubeToolboxData || {};
    setSettings({ ...defaultSettings, ...data });

    const container = document.getElementById('kalicube-toolbox-button');
    if (container) {
      if ((isVisible || settings.scroll_animation_enabled === 'no') && settings.button_enabled !== 'no') {
        container.classList.add('activated');
      } else {
        container.classList.remove('activated');
      }
    }
  }, [isVisible, settings.scroll_animation_enabled, settings.button_enabled]);
  
  // If button is disabled globally, don't render anything
  if (settings.button_enabled === 'no') {
    return null;
  }
  
  /**
   * Darkens a color by a specified percentage
   * @param color - Hex color to darken
   * @param amount - Percentage to darken by (0-1)
   */
  const darkenColor = (color: string, amount: number = 0.1): string => {
    // Convert hex to RGB
    let r = parseInt(color.substring(1, 3), 16);
    let g = parseInt(color.substring(3, 5), 16);
    let b = parseInt(color.substring(5, 7), 16);
    
    // Darken by amount
    r = Math.max(0, Math.floor(r * (1 - amount)));
    g = Math.max(0, Math.floor(g * (1 - amount)));
    b = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Convert back to hex
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };
  
  /**
   * Formats padding or margin with units
   * @param values - Directional values object
   */
  const formatDirectionalValues = (values: DirectionalValue): string => {
    const { top, right, bottom, left, unit } = values;
    return `${top}${unit} ${right}${unit} ${bottom}${unit} ${left}${unit}`;
  };
  
  /**
   * Gets position styles based on button position and margin
   */
  const getPositionStyles = (): React.CSSProperties => {
    const position = settings.button_position || defaultSettings.button_position;
    const margin = settings.button_margin || defaultSettings.button_margin;
    const { top, right, bottom, left, unit } = margin;
    
    switch (position) {
      case 'top-left':
        return {
          top: `${top}${unit}`,
          left: `${left}${unit}`,
          right: 'auto',
          bottom: 'auto'
        };
      case 'top-right':
        return {
          top: `${top}${unit}`,
          right: `${right}${unit}`,
          left: 'auto',
          bottom: 'auto'
        };
      case 'bottom-left':
        return {
          bottom: `${bottom}${unit}`,
          left: `${left}${unit}`,
          top: 'auto',
          right: 'auto'
        };
      case 'bottom-right':
      default:
        return {
          bottom: `${bottom}${unit}`,
          right: `${right}${unit}`,
          top: 'auto',
          left: 'auto'
        };
    }
  };
  
  // Calculate button styles based on settings
  const buttonStyle: React.CSSProperties = {
    backgroundColor: hover 
      ? darkenColor(settings.button_color || defaultSettings.button_color) 
      : settings.button_color || defaultSettings.button_color,
    color: settings.text_color || defaultSettings.text_color,
    padding: formatDirectionalValues(settings.button_padding || defaultSettings.button_padding),
    fontSize: `${settings.font_size || defaultSettings.font_size}px`,
    borderRadius: `${settings.border_radius || defaultSettings.border_radius}px`,
    boxShadow: settings.box_shadow === 'yes' ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
    zIndex: settings.z_index || defaultSettings.z_index,
    ...getPositionStyles()
  };
  
  // Only apply animation class when becoming visible and animation is enabled
  const animationClass = (settings.animation_type !== 'none' && shouldAnimate)
    ? `animate-${settings.animation_type}` 
    : '';
  
  // Apply scroll animation class with visibility state
  const scrollClass = settings.scroll_animation_enabled === 'yes' 
    ? `scroll-animation${isVisible ? ' show' : ' hide'}` 
    : '';
  
  return (
    <a
      href={settings.button_url || '#'}
      className={`kalicube-floating-button ${settings.button_position || 'bottom-right'} ${animationClass} ${scrollClass}`}
      style={buttonStyle}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {settings.button_icon && (
        <i className={`${settings.button_icon} button-icon`}></i>
      )}
      <span>{settings.button_text || 'Contact Us'}</span>
    </a>
  );
};

export default FloatingButton;